import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowRight, Clock, CheckCircle2, Navigation, Loader2, List, Map as MapIcon, Calendar as CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { api, STATUS_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TaskMap from "../components/TaskMap";

export default function DashboardClient() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [coords, setCoords] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const [view, setView] = useState("list");

  useEffect(() => {
    api.get("/tasks?mine=client").then((r) => setTasks(r.data)).catch(() => {});
  }, []);

  const askLocation = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non supportée"); return; }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success("Position activée");
        setGeoBusy(false);
      },
      () => { toast.error("Position refusée"); setGeoBusy(false); },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  const active = tasks.filter((t) => !["paid_out", "cancelled"].includes(t.status));
  const done = tasks.filter((t) => t.status === "paid_out");
  const geoTasks = tasks.filter((t) => t.client_lat != null && t.client_lng != null);

  return (
    <div className="space-y-8">
      <section className="bg-gradient-to-br from-navy to-[#0F2342] text-white rounded-3xl p-6 md:p-10 relative overflow-hidden noise">
        <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-lime/20 blur-3xl" />
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <span className="chip-lime">Client</span>
            <h1 className="text-3xl md:text-4xl font-heading font-black mt-3 text-white">Salut {user.full_name.split(" ")[0]} 👋</h1>
            <p className="text-white/80 mt-1">Besoin d'aide aujourd'hui ? Publie ta tâche en 30 secondes.</p>
            {(user.client_credits || 0) > 0 && (
              <Link to="/referral" className="inline-flex items-center gap-2 mt-4 bg-lime/20 hover:bg-lime/30 text-lime border border-lime/40 rounded-full px-4 py-2 text-sm font-bold transition-colors" data-testid="dashboard-credits-badge">
                💸 {user.client_credits.toFixed(2)} $ de crédit · appliqué auto
              </Link>
            )}
          </div>
          <Link to="/tasks/new" className="btn-primary px-6 py-4" data-testid="dashboard-new-task">
            <Plus size={18} /> Nouvelle tâche
          </Link>
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-2xl font-heading font-black text-navy">Tâches en cours</h2>
          <div className="flex items-center gap-2">
            <Link to="/calendar" className="text-sm font-bold text-navy hover:text-lime inline-flex items-center gap-1" data-testid="client-cal-link">
              <CalendarIcon size={14} /> Calendrier
            </Link>
            <span className="text-sm text-[#475569]">{active.length} active(s)</span>
            <div className="flex items-center gap-1 p-1 bg-surface rounded-full" data-testid="client-view-toggle">
              <button onClick={() => setView("list")} data-testid="client-view-list"
                className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 ${view === "list" ? "bg-navy text-white" : "text-navy"}`}>
                <List size={12} /> Liste
              </button>
              <button onClick={() => setView("map")} data-testid="client-view-map"
                className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 ${view === "map" ? "bg-navy text-white" : "text-navy"}`}>
                <MapIcon size={12} /> Carte
              </button>
            </div>
          </div>
        </div>

        {view === "map" ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {!coords ? (
                <button onClick={askLocation} disabled={geoBusy} className="btn-outline py-2 px-4 text-sm" data-testid="client-enable-geo">
                  {geoBusy ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />}
                  {geoBusy ? "..." : "Activer ma position"}
                </button>
              ) : (
                <span className="chip-lime"><Navigation size={12} /> Position activée</span>
              )}
              <span className="text-xs text-[#475569]">{geoTasks.length} tâche(s) géolocalisée(s)</span>
            </div>
            {geoTasks.length === 0 && !coords ? (
              <div className="card-surface p-8 text-center text-[#475569]">
                Aucune tâche géolocalisée. Active ta position lors de la création pour les voir ici.
              </div>
            ) : (
              <TaskMap center={coords} tasks={geoTasks} radiusKm={null} />
            )}
          </div>
        ) : active.length === 0 ? (
          <div className="card-surface p-8 text-center">
            <div className="h-14 w-14 mx-auto rounded-full bg-surface flex items-center justify-center"><Clock className="text-navy" /></div>
            <p className="mt-3 font-bold text-navy">Aucune tâche active</p>
            <p className="text-sm text-[#475569] mt-1">Publie ta première demande pour voir la magie opérer.</p>
            <Link to="/tasks/new" className="btn-primary mt-4 inline-flex" data-testid="empty-new-task">Publier une tâche</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {active.map((t) => <TaskCard key={t.id} task={t} />)}
          </div>
        )}
      </section>

      {done.length > 0 && (
        <section>
          <h2 className="text-2xl font-heading font-black text-navy mb-4">Tâches complétées</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {done.slice(0, 4).map((t) => <TaskCard key={t.id} task={t} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function TaskCard({ task }) {
  const s = STATUS_LABELS[task.status];
  return (
    <Link to={`/tasks/${task.id}`} className="card-interactive p-5 block" data-testid={`task-card-${task.id}`}>
      <div className="flex items-center justify-between">
        <span className={`chip ${s.color}`}>{s.fr}</span>
        {task.urgent && <span className="chip-lime">Urgent</span>}
      </div>
      <h3 className="font-heading font-black text-navy text-xl mt-3 line-clamp-1">{task.title}</h3>
      <p className="text-sm text-[#475569] line-clamp-2 mt-1">{task.description}</p>
      <div className="flex items-end justify-between mt-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-[#94A3B8]">Total</div>
          <div className="font-heading font-black text-2xl text-navy">{task.total.toFixed(2)} $</div>
        </div>
        <ArrowRight size={18} className="text-navy" />
      </div>
      {task.helper_name && (
        <div className="mt-3 pt-3 border-t border-[#E2E8F0] text-sm text-[#475569] flex items-center gap-2">
          <CheckCircle2 size={14} className="text-lime" /> Avec {task.helper_name}
        </div>
      )}
    </Link>
  );
}
